import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, PenLine, Loader2 } from "lucide-react";
import { buildSignatureHtml } from "../../lib/signature";

export const SignatureModal = ({ api, user, accounts = [], onClose, onSaved }) => {
  const list = accounts.length ? accounts.map((a) => a.email) : [user.email];
  const [account, setAccount] = useState(user.email);
  const [form, setForm] = useState({ name: "", designation: "", mobile: "" });
  const [avatarUrl, setAvatarUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // load the selected mailbox's signature
  useEffect(() => {
    setLoading(true);
    setForm({ name: "", designation: "", mobile: "" });
    api.get("/mail/signature", { params: { account } })
      .then(({ data }) => { if (data.signature) setForm(data.signature); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, account]);

  // avatar preview only for the currently signed-in mailbox
  useEffect(() => {
    if (account !== user.email) { setAvatarUrl(""); return; }
    api.get("/mail/profile")
      .then(({ data }) => { if (data.avatar) setAvatarUrl(`https://nexsecure.in/api/mail/avatar/${user.email}.jpg`); else setAvatarUrl(""); })
      .catch(() => setAvatarUrl(""));
  }, [api, account, user.email]);

  const html = useMemo(
    () => buildSignatureHtml({
      name: form.name || "Your Name",
      designation: form.designation || "Your Designation",
      mobile: form.mobile,
      email: account,
      avatar: avatarUrl,
    }),
    [form, account, avatarUrl]
  );

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/mail/signature", form, { params: { account } });
      toast.success(`Signature saved for ${account}`);
      onSaved?.(data.signature, account);
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save signature");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "w-full bg-white border border-ol-line text-ol-text placeholder:text-ol-sub px-4 py-2.5 text-[13px] rounded-[4px] focus:outline-none focus:border-ol-blue transition-colors";
  const label = "block text-[11px] tracking-[0.08em] uppercase text-ol-sub mb-1.5";

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6" style={{ fontFamily: '"Segoe UI", system-ui, sans-serif' }} data-testid="signature-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white shadow-2xl rounded-lg max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-ol-blue text-white shrink-0">
          <div className="flex items-center gap-3">
            <PenLine size={18} />
            <div>
              <h3 className="text-[15px] font-semibold">Mailbox signature</h3>
              <p className="text-[11px] text-white/80">Each mailbox keeps its own — it's added automatically based on the From account</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/10 rounded-[4px] p-1 transition-colors" aria-label="Close" data-testid="signature-modal-close"><X size={18} /></button>
        </div>

        <div className="px-6 pt-4">
          <label className={label}>Mailbox</label>
          <select value={account} onChange={(e) => setAccount(e.target.value)} className={`${input} cursor-pointer`} data-testid="msig-account-select">
            {list.map((em) => (<option key={em} value={em}>{em}</option>))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-ol-sub text-[13px]"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : (
          <form onSubmit={save} className="grid md:grid-cols-2 gap-6 p-6 overflow-y-auto">
            <div className="flex flex-col gap-4">
              <div>
                <label htmlFor="msig-name" className={label}>Full name</label>
                <input id="msig-name" required className={input} placeholder="e.g. Satheesh K Menon" value={form.name} onChange={set("name")} data-testid="msig-name-input" />
              </div>
              <div>
                <label htmlFor="msig-designation" className={label}>Designation</label>
                <input id="msig-designation" required className={input} placeholder="e.g. CEO & MD" value={form.designation} onChange={set("designation")} data-testid="msig-designation-input" />
              </div>
              <div>
                <label htmlFor="msig-mobile" className={label}>Mobile (optional)</label>
                <input id="msig-mobile" className={input} placeholder="e.g. +91 98XXXXXX00" value={form.mobile} onChange={set("mobile")} data-testid="msig-mobile-input" />
              </div>
              <button type="submit" disabled={busy} className="mt-2 flex items-center justify-center gap-2 bg-ol-blue hover:bg-ol-bluehover text-white font-semibold text-[13px] px-8 py-2.5 rounded-[4px] transition-colors disabled:opacity-60" data-testid="msig-save-button">
                {busy ? <Loader2 size={15} className="animate-spin" /> : null}
                {busy ? "Saving…" : "Save signature"}
              </button>
            </div>
            <div>
              <p className={label}>Live preview</p>
              <div className="bg-[#f3f2f1] border border-ol-line rounded-[4px] p-4 overflow-x-auto" data-testid="msig-preview">
                <div style={{ minWidth: 470 }} dangerouslySetInnerHTML={{ __html: html }} />
              </div>
              <p className="mt-3 text-[11px] text-ol-sub leading-relaxed">Company numbers, address, logo and disclaimer stay fixed so the whole team looks consistent.</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
